import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Carregando from '@/components/Carregando';
import BotaoCopiar from '@/components/relatorios/BotaoCopiar';
import Acumulado from '@/components/produtividade/Acumulado';
import Grade from '@/components/produtividade/Grade';
import { useAuth } from '@/auth/AuthProvider';
import { useLeads } from '@/leads/LeadsProvider';
import { temFuncao } from '@/equipe/funcoes';
import { diaDaData } from '@/produtividade/dia';
import { JANELA_PADRAO_DIAS, janelaDeDias, taxasAcumuladas } from '@/produtividade/acumulado';
import { domingoDaSemana } from '@/produtividade/grade';
import { semanaDe } from '@/produtividade/placar';
import { gradeDaSemana, gradeDoMes, mesesDosDias, resumoDeHoje } from '@/produtividade/indicadores';
import { faixaEntreDiasNaClinica, mesNaClinica } from '@/dashboard/periodo';
import { resumoDoDia } from '@/relatorios/formato';
import { buscarAgendamentosDoMes, buscarInteracoesDoMes, buscarInteracoesEntre } from '@/services/produtividadeService';
import { buscarDadosDoResumo } from '@/services/resumoDeHojeService';
import DialogoMetasIndividuais from '@/components/metas/DialogoMetasIndividuais';
import { ouvirMetas } from '@/services/metasService';
import { normalizarMetasPessoa } from '@/metas/calculo';
import { vendedores } from '@/equipe/funcoes';
import { expedienteDaClinica } from '@/produtividade/expediente';
import { feriadosDaClinica } from '@/produtividade/feriados';
import { AvisoMetas } from '@/components/AvisoCarga';

/**
 * ★★ WO lote 2b — INDICADORES DE PERFORMANCE: a grade mensal.
 *
 * A pergunta que o dono não conseguia responder: *"o que a Jéssica fez hoje?"* — e, no
 * desenho aprovado em 3/out, *"como foi a semana e o mês?"*. A tela responde com o que o
 * **sistema gravou sozinho**, com autor e data; nada que a equipe digite entra aqui.
 *
 * ★ Era `ProdutividadePage`. Mudou de forma (dia → grade mensal) e de nome, por decisão do
 * dono sobre `docs/wo-desenho-aprovado.html`. ⚠ `/produtividade` continua existindo e
 * **redireciona** — a rota foi ao ar em 2/out e o gestor já a abriu.
 *
 * ★ A CONTA NÃO MORA NESTA TELA. Ela chama `painelDoDia` (puro, 28 casos de teste) e
 * apresenta o resultado. Duas telas discordando sobre o dia de alguém é a pior conversa
 * possível numa equipe — foi o motivo de a casa ter unificado o comparecimento na WK.
 *
 * ★★ QUEM VÊ O QUÊ, e as três camadas:
 *   1. o MENU só mostra o item a `gestor` e `sdr` (`AppShell`);
 *   2. a ROTA está sob `RequireAuth papeis={VENDAS}`;
 *   3. ★ AQUI: quem não é gestor nem superadmin vê **só a própria linha** — o filtro é por
 *      `usuario.uid`, não por nome.
 *
 * ⚠ A RECEPÇÃO NÃO ENTRA, e não é esquecimento. ★ Medido: a regra de `leads` é `podeVendas`,
 * então a recepcionista **não consegue ler** os leads de que a conta depende, e a regra de
 * `collectionGroup` das interações é `papel in ['gestor','sdr']`. Incluí-la exigiria um
 * agregado pré-calculado no servidor, e afrouxar a regra de `leads` foi explicitamente
 * recusado na VB. ★ É a MESMA decisão, com as MESMAS palavras, que o `PlacarPage` já traz.
 *
 * ★★★ INDICADORES (01/11/2026) — A TELA TRAVAVA, e o conserto não mudou número nenhum.
 *
 * Medido com o volume de Campinas (8.884 leads, 20.638 interações no mês), CPU 4× mais lenta:
 * a tela levava 60 s para aparecer, com uma trava única de 16,8 s, e refazia a conta a cada
 * lead alterado. ★ O que mudou (`docs/indicadores-testes.md`):
 *   1. o RESUMO DE HOJE tem consulta própria, só do dia, e aparece antes da grade;
 *   2. a grade abre na SEMANA ATUAL (decisão do dono); o mês fica a um clique;
 *   3. consulta que não chega do servidor vira ERRO na tela, nunca grade zerada nem "Sem
 *      movimento hoje" (`soServidor`);
 *   4. a conta usa os leads ADIADOS (`useDeferredValue`): a alteração de um lead pinta a tela
 *      primeiro e recalcula depois.
 *
 * ⚠ DINHEIRO SEGUE A FUNÇÃO, não o acesso (a separação da WJ). `Fechou`/`Valor` aparecem
 * para o gestor e para quem exerce a função de **negociador**; para a SDR, não. ★ É a mesma
 * regra do resumo copiado, que o dono decidiu — e ela mora num lugar só, em
 * `relatorios/formato.js`. Há guarda de grafo provando que esta tela não alcança
 * `remuneracao/*` nem `messaging/canais.js`.
 */

/** ★ Toda leitura desta tela exige o servidor: sem ele, erro — nunca a cópia local calada. */
const SO_SERVIDOR = { soServidor: true };

/** ★ As metas por pessoa de um documento de mês, normalizadas pela régua única. */
const metasDoDocumento = (doc) => {
  const saida = {};
  for (const [uid, m] of Object.entries(doc?.metasPorSdr ?? {})) saida[uid] = normalizarMetasPessoa(m);
  return saida;
};

export default function IndicadoresPage() {
  const [falhaMetas, setFalhaMetas] = useState(false); // ★ WT lote 1b
  const { usuario, isGestor, isSuperadmin } = useAuth();
  const { clinicaId, clinica, leads, equipe, carregando: carregandoLeads } = useLeads();
  /**
   * ★★ 1.5 — A CONTA USA OS LEADS ADIADOS. Um lead alterado chega aqui como uma lista NOVA
   * inteira (o `onSnapshot` remonta os 8.884 objetos), e a grade dependia dela. ★ Com o valor
   * adiado, o React pinta primeiro e recalcula numa renderização de prioridade baixa.
   */
  const leadsAdiados = useDeferredValue(leads);

  /** ★★ 1.7 — decisão do dono (01/11): a grade abre na SEMANA ATUAL; o mês fica a um clique. */
  const [visao, setVisao] = useState('semana');
  const [mes, setMes] = useState(() => mesNaClinica(new Date()));
  const hoje = diaDaData(new Date());
  /**
   * ★ A semana é a do Placar (`semanaDe`): segunda a sábado, e o DOMINGO pertence à semana que
   * acabou — a mesma convenção da Agenda (`limitesDaSemana`, segunda a domingo). ★ A grade só não
   * tem coluna de domingo; a consulta vai até o domingo 23:59:59,999, para o aviso "caíram em
   * domingo" continuar contando.
   */
  const inicioDaSemana = semanaDe(hoje)[0];
  const semana = useMemo(() => semanaDe(inicioDaSemana), [inicioDaSemana]);
  const chaveDaGrade = visao === 'semana' ? `semana:${inicioDaSemana}` : `mes:${mes}`;

  const [dadosGrade, setDadosGrade] = useState(null);
  const [erroGrade, setErroGrade] = useState(null);
  const [tentativaGrade, setTentativaGrade] = useState(0);
  const [dadosDia, setDadosDia] = useState(null);
  const [erroDia, setErroDia] = useState(null);
  const [tentativaDia, setTentativaDia] = useState(0);
  /**
   * ★★ O ACUMULADO É SOB DEMANDA, e isto é decisão de custo medida.
   *
   * ⚠ A janela de 90 dias são **4.816** interações, contra as **1.993** do mês. Carregá-la
   * junto com a grade mais que dobraria o que a tela baixa — para uma seção que o gestor
   * abre de vez em quando. ★ Então: fechada por padrão, e **nada é buscado antes do clique**.
   *
   * ★ `jaBuscou` evita refazer a consulta ao fechar e reabrir a seção.
   */
  /**
   * ★★★ CONSERTO DE UM DEFEITO MEU DO LOTE 2b, que está NO AR.
   *
   * A tela lia `clinica.config.metasPorSdr` — e `metasPorSdr` **não mora lá**: mora no
   * documento do MÊS (`clinicas/{id}/metas/{AAAA-MM}`), que é o que `consolidarPeriodo`
   * chama de `config` ao fazer `{ ...METAS_PADRAO, ...metas }`.
   *
   * ⚠ Conferido em produção: a config da clínica tem `funil`, `origens`, `procedimentos`…
   * e **nenhum `metasPorSdr`**. O acumulado, portanto, nunca achava meta nenhuma e mostrava
   * "sem meta configurada" mesmo com a meta preenchida. ★ Agora lê do lugar certo.
   */
  const [metasPorMesCru, setMetasPorMesCru] = useState({});
  const [acumuladoAberto, setAcumuladoAberto] = useState(false);
  /** ★★★ A-7a — o diálogo de metas individuais; o botão que o abre é `{isGestor && …}`. */
  const [metasIndividuaisAberto, setMetasIndividuaisAberto] = useState(false);
  const [interacoes90, setInteracoes90] = useState(null);
  const [erro90, setErro90] = useState(null);
  /** ★ Na semana, o acumulado ainda usa os agendamentos do MÊS escolhido, como sempre usou. */
  const [agsDoAcumulado, setAgsDoAcumulado] = useState(null);

  /**
   * ★★ 1.3 — O RESUMO DE HOJE, com consulta própria, só do dia (`buscarDadosDoResumo`). Sai PRIMEIRO.
   *
   * ⚠ Os leads entram pelo `ref`: o resumo é lido UMA vez por dia, não a cada lead alterado.
   *
   * ⚠⚠ Medido no emulador a 4×: com a grade pedida antes, o resumo chegou DEPOIS dela (16,5 s
   * contra 12,0 s) — as respostas dividem o mesmo canal. ★ Por isso a grade só é pedida depois
   * da primeira resposta do resumo (`liberarGrade`), com sucesso ou erro.
   */
  const leadsAtuais = useRef(leads);
  leadsAtuais.current = leads;
  const [liberarGrade, setLiberarGrade] = useState(false);
  useEffect(() => {
    if (!clinicaId || carregandoLeads) return undefined;
    let valendo = true;
    setDadosDia(null);
    setErroDia(null);
    buscarDadosDoResumo(clinicaId, () => leadsAtuais.current)
      .then((dados) => { if (valendo) setDadosDia(dados); })
      .catch((e) => {
        if (!valendo) return;
        console.error('[OdontoSDR] Falha ao carregar o resumo de hoje:', e);
        setErroDia('Não foi possível carregar o resumo de hoje: o servidor não respondeu. Não há o que copiar até carregar.');
      })
      .finally(() => { if (valendo) setLiberarGrade(true); });
    return () => { valendo = false; };
  }, [clinicaId, carregandoLeads, hoje, tentativaDia]);

  /**
   * ★★ A GRADE: na semana, as interações da segunda ao domingo + os agendamentos do(s) mês(es)
   * da semana; no mês, o que sempre leu. ⚠ `chave` acompanha os dados: ao trocar de visão, a
   * renderização seguinte ainda tem os dados da anterior, e eles não podem ser montados como
   * se fossem da nova.
   */
  useEffect(() => {
    if (!clinicaId || !liberarGrade) return undefined;
    let valendo = true;
    setDadosGrade(null);
    setErroGrade(null);
    const meses = visao === 'semana' ? mesesDosDias(semana) : [mes];
    const { de, ate } = faixaEntreDiasNaClinica(semana[0], domingoDaSemana(semana));
    Promise.all([
      visao === 'semana'
        ? buscarInteracoesEntre(clinicaId, de, ate, SO_SERVIDOR)
        : buscarInteracoesDoMes(clinicaId, mes, SO_SERVIDOR),
      Promise.all(meses.map((m) => buscarAgendamentosDoMes(clinicaId, m, SO_SERVIDOR))),
    ])
      .then(([is, ags]) => {
        if (!valendo) return;
        setDadosGrade({
          chave: chaveDaGrade,
          interacoes: is,
          agendamentosPorMes: Object.fromEntries(meses.map((m, i) => [m, ags[i]])),
        });
      })
      .catch((e) => {
        if (!valendo) return;
        console.error('[OdontoSDR] Falha ao carregar os indicadores:', e);
        setErroGrade(visao === 'semana'
          ? 'Não foi possível carregar a semana: o servidor não respondeu. Os números não aparecem até carregar.'
          : 'Não foi possível carregar o mês: o servidor não respondeu. Os números não aparecem até carregar.');
      });
    // ★ `valendo` evita que a resposta de um período antigo sobrescreva a de um novo quando o
    // usuário troca antes de a primeira consulta voltar.
    return () => { valendo = false; };
  }, [clinicaId, liberarGrade, visao, mes, semana, chaveDaGrade, tentativaGrade]);

  /** ★ As metas de cada mês que a tela mostra: o escolhido e, na semana, o(s) dela. */
  const chaveDasMetas = [...new Set([mes, ...(visao === 'semana' ? mesesDosDias(semana) : [])])].join(',');
  useEffect(() => {
    if (!clinicaId) return undefined;
    // ★★ WT 1b: era só console — a grade saía sem cor e parecia "sem meta configurada".
    const cancelar = chaveDasMetas.split(',').map((m) => ouvirMetas(
      clinicaId, m, (doc) => setMetasPorMesCru((antes) => ({ ...antes, [m]: doc })), () => setFalhaMetas(true),
    ));
    return () => { for (const c of cancelar) c?.(); };
  }, [clinicaId, chaveDasMetas]);
  const metasDoMes = metasPorMesCru[mes] ?? null;

  useEffect(() => {
    // ★★ A GUARDA QUE IMPORTA: sem o clique, nenhuma consulta sai.
    if (!acumuladoAberto || !clinicaId || interacoes90 !== null) return undefined;
    let valendo = true;
    const { de, ate } = janelaDeDias(JANELA_PADRAO_DIAS, new Date());
    buscarInteracoesEntre(clinicaId, de, ate, SO_SERVIDOR)
      .then((is) => { if (valendo) setInteracoes90(is); })
      .catch((e) => {
        if (!valendo) return;
        console.error('[OdontoSDR] Falha ao carregar o acumulado de 90 dias:', e);
        setInteracoes90([]);
        setErro90('Não foi possível carregar o acumulado.');
      });
    return () => { valendo = false; };
  }, [acumuladoAberto, clinicaId, interacoes90]);

  useEffect(() => {
    if (!acumuladoAberto || !clinicaId || visao !== 'semana' || agsDoAcumulado?.mes === mes) return undefined;
    let valendo = true;
    buscarAgendamentosDoMes(clinicaId, mes, SO_SERVIDOR)
      .then((lista) => { if (valendo) setAgsDoAcumulado({ mes, lista }); })
      .catch((e) => {
        if (!valendo) return;
        console.error('[OdontoSDR] Falha ao carregar o acumulado de 90 dias:', e);
        setErro90('Não foi possível carregar o acumulado.');
      });
    return () => { valendo = false; };
  }, [acumuladoAberto, clinicaId, visao, mes, agsDoAcumulado]);

  /**
   * ★ Os telefones da PRÓPRIA EQUIPE, a excluir.
   *
   * ⚠⚠ LACUNA CONHECIDA, MEDIDA E DITA: os 9 documentos de `usuarios` têm `ramal`, **não
   * `telefone`**, e `config.telefonesEquipe` é `null` em produção — então esta lista chega
   * VAZIA e o filtro, embora ligado, não exclui ninguém. ★ Conferido: nos 17 leads que a
   * Jéssica ligou em 21/09, nenhum é da equipe. É cobertura FUTURA, não erro atual; o campo
   * entra em Configurações no lote 3.
   */
  const telefonesEquipe = useMemo(
    () => (Array.isArray(clinica?.config?.telefonesEquipe) ? clinica.config.telefonesEquipe : []),
    [clinica],
  );
  // ★ O expediente da clínica, com o padrão como RESERVA DIA A DIA — config pela metade
  // não zera a meta dos dias que faltam. ⚠ Em `useMemo`: sem ele, clínica COM expediente
  // ganhava um objeto novo a cada renderização, e a grade era recalculada a cada uma.
  const config = useMemo(() => expedienteDaClinica(clinica?.config), [clinica?.config]);
  // ★★ 02/11/2026 — os feriados sem meta, do bloqueio de dia inteiro da agenda (uma fonte só)
  const feriados = useMemo(() => feriadosDaClinica(clinica?.config), [clinica?.config]);
  /** ★★ As metas por pessoa vêm do documento do MÊS, normalizadas pela régua única. */
  const metasPorPessoa = useMemo(() => metasDoDocumento(metasDoMes), [metasDoMes]);
  const metasPorMes = useMemo(
    () => Object.fromEntries(mesesDosDias(semana).map((m) => [m, metasDoDocumento(metasPorMesCru[m])])),
    [semana, metasPorMesCru],
  );

  const vejoTudo = Boolean(isGestor || isSuperadmin);
  const euNaEquipe = useMemo(
    () => (equipe ?? []).find((u) => u.id === usuario?.uid) ?? null,
    [equipe, usuario?.uid],
  );
  const mostrarDinheiro = vejoTudo || temFuncao(euNaEquipe, 'negociador');

  /**
   * ★★ A PRIVACIDADE É APLICADA NA EQUIPE, ANTES DA GRADE — e isso não é detalhe.
   *
   * Filtrar as linhas DEPOIS de montar a grade deixaria os TOTAIS do bloco contando os
   * colegas: a SDR veria a própria linha e um "Total" maior que ela, que é precisamente o
   * comparativo que a tela não deve dar.
   */
  const equipeVisivel = useMemo(
    () => (vejoTudo ? (equipe ?? []) : (equipe ?? []).filter((u) => u.id === usuario?.uid)),
    [equipe, vejoTudo, usuario?.uid],
  );

  const dadosDaGrade = dadosGrade?.chave === chaveDaGrade ? dadosGrade : null;
  const grade = useMemo(() => {
    if (!dadosDaGrade) return null;
    const comum = {
      interacoes: dadosDaGrade.interacoes, leads: leadsAdiados, equipe, equipeVisivel,
      telefonesEquipe, hoje, config, feriados, agora: new Date(),
      // ★ o aviso de domingo: gestor conta a equipe; a SDR, só os registros dela
      verTodas: vejoTudo,
    };
    return visao === 'semana'
      ? gradeDaSemana({ ...comum, semana, agendamentosPorMes: dadosDaGrade.agendamentosPorMes, metasPorMes })
      : gradeDoMes({ ...comum, mes, agendamentos: dadosDaGrade.agendamentosPorMes[mes], metas: metasPorPessoa });
  }, [dadosDaGrade, visao, semana, mes, leadsAdiados, equipeVisivel, equipe, telefonesEquipe, hoje, config, feriados, vejoTudo, metasPorMes, metasPorPessoa]);

  /**
   * Para o resumo copiado: o painel do dia de HOJE, a partir só dos dados do dia.
   * ★★ A2 — só as linhas da equipe visível; o gestor (`vejoTudo`) vê todas.
   */
  const linhasDeHoje = useMemo(() => {
    if (!dadosDia) return null;
    return resumoDeHoje({
      dia: dadosDia.dia, interacoes: dadosDia.interacoes, agendamentos: dadosDia.agendamentos,
      leads: leadsAdiados, equipeVisivel, telefonesEquipe, agora: new Date(), verTodas: vejoTudo,
    });
  }, [dadosDia, leadsAdiados, equipeVisivel, telefonesEquipe, vejoTudo]);

  /**
   * ★★ A2 — O TEXTO COPIADO É LIDO DE NOVO NO SERVIDOR, na hora do clique.
   *
   * ⚠ O resumo da tela é lido UMA vez, ao abrir. Quem abre de manhã e copia à tarde colava no
   * grupo o número da manhã. ★ O clique refaz as consultas do dia (`buscarDadosDoResumo`, as
   * mesmas da abertura, com `soServidor`), atualiza a tela e copia o texto novo. Se a busca
   * falhar, o `BotaoCopiar` mostra o erro e não copia nada.
   */
  const textoAtualizadoDe = async (id) => {
    let dados;
    try {
      dados = await buscarDadosDoResumo(clinicaId, () => leadsAtuais.current);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao atualizar o resumo de hoje para copiar:', e);
      throw e;
    }
    setDadosDia(dados);
    const linha = resumoDeHoje({
      dia: dados.dia, interacoes: dados.interacoes, agendamentos: dados.agendamentos,
      leads: leadsAtuais.current, equipeVisivel, telefonesEquipe, agora: new Date(), verTodas: vejoTudo,
    }).find((x) => x.id === id);
    if (!linha) throw Object.assign(new Error('sem movimento hoje'), { paraTela: 'Sem movimento hoje nesta linha — nada foi copiado.' });
    return resumoDoDia(linha, dados.dia, {
      // ★ A FUNÇÃO decide qual dos dois formatos, pessoa por pessoa.
      ehNegociador: temFuncao((equipe ?? []).find((u) => u.id === id) ?? null, 'negociador'),
    });
  };

  const agendamentosDoAcumulado = visao === 'mes'
    ? dadosDaGrade?.agendamentosPorMes?.[mes] ?? null
    : (agsDoAcumulado?.mes === mes ? agsDoAcumulado.lista : null);
  const acumulado = useMemo(() => {
    if (!acumuladoAberto || interacoes90 === null || agendamentosDoAcumulado === null) return null;
    return taxasAcumuladas({
      dias: Number(clinica?.config?.janelaTaxasDias) || JANELA_PADRAO_DIAS,
      ate: new Date(),
      interacoes: interacoes90,
      leads: leadsAdiados,
      agendamentos: agendamentosDoAcumulado,
      equipe: equipeVisivel,
      metasPorSdr: metasPorPessoa,   // ★ do documento do MÊS, não da config da clínica
      // ★★ A-2c: a meta de agendamentos virou POR DIA — a janela precisa do expediente para
      // ⚠ somá-la pelos dias (sábado meio, domingo zero), como as ligações.
      expediente: config,
      feriados,
    });
  }, [acumuladoAberto, interacoes90, leadsAdiados, agendamentosDoAcumulado, equipeVisivel, clinica, metasPorPessoa, feriados]);

  /** ★ Mês futuro não aparece (decisão H-4-3): o seletor para no mês atual. */
  const mesMaximo = mesNaClinica(new Date());

  if (carregandoLeads) return <Carregando />;

  return (
    <section className="pagina">
      <header className="pagina-cabecalho">
        <h1>Indicadores de Performance</h1>
        {/* ★★ WT 1b — a faixa não empurra a grade (`role="status"`, uma linha). */}
        {falhaMetas && <AvisoMetas />}
        <div className="grupo-botoes" role="group" aria-label="Período da grade">
          <button
            type="button"
            className={visao === 'semana' ? 'btn-primario compacto' : 'btn-secundario compacto'}
            aria-pressed={visao === 'semana'}
            onClick={() => setVisao('semana')}
          >
            Semana atual
          </button>
          <button
            type="button"
            className={visao === 'mes' ? 'btn-primario compacto' : 'btn-secundario compacto'}
            aria-pressed={visao === 'mes'}
            onClick={() => setVisao('mes')}
          >
            Mês
          </button>
        </div>
        {visao === 'mes' && (
          <input
            type="month"
            aria-label="Mês"
            value={mes}
            max={mesMaximo}
            onChange={(e) => setMes(e.target.value || mesMaximo)}
          />
        )}
        {/* ★★★ A-7a — METAS INDIVIDUAIS, SÓ GESTOR.
            ⚠⚠ A SDR não vê o botão **nem o diálogo**: a rota desta tela é aberta a ela de
            propósito (ela vê a própria linha), então a trava tem de ser aqui. ★ E as rules
            recusam a escrita de qualquer forma — a tela é a primeira camada, não a única. */}
        {isGestor && (
          <button
            type="button"
            className="btn-secundario compacto"
            onClick={() => setMetasIndividuaisAberto(true)}
          >
            Metas individuais
          </button>
        )}
      </header>

      {/* ★★ O AVISO É FIXO, e fica na tela — não num tooltip, não num rodapé que ninguém lê.
          É o contrato da peça: a regra de ouro é que nada aqui pode ser burlado, e quem lê o
          número precisa saber de onde ele vem. */}
      <p className="aviso">
        <strong>De onde vêm estes números.</strong> Todos foram gravados pelo próprio sistema,
        com autor e data, no momento em que a ação aconteceu — ninguém os digita e ninguém os
        edita depois (a linha do tempo é à prova de apagamento por regra do banco).{' '}
        <strong>Ligações</strong> conta pessoas distintas, então clicar duas vezes no mesmo
        lead não muda o número. Interações do WhatsApp automático, sem pessoa, não entram.
        Não há taxa nenhuma na grade: num único dia os números são pequenos demais e uma
        porcentagem aqui seria ruído — as taxas moram no acumulado de 90 dias.
      </p>

      {/* ★★ 1.3 — o resumo vem PRIMEIRO: é o que a equipe abre a tela para copiar de manhã. */}
      <section className="secao">
        <h2 className="secao-titulo">Resumo de hoje para copiar</h2>
        <p className="secao-sub">
          Texto curto para mandar no WhatsApp. {mostrarDinheiro
            ? 'Quem exerce a função de negociador recebe o resumo com valor fechado; a SDR, o de ligações e agendamentos.'
            : 'O seu é o de ligações e agendamentos.'}
        </p>
        {erroDia ? (
          <div className="erro" role="alert">
            <p>{erroDia}</p>
            <button type="button" className="btn-secundario compacto" onClick={() => setTentativaDia((n) => n + 1)}>
              Tentar de novo
            </button>
          </div>
        ) : linhasDeHoje === null ? (
          <Carregando texto="Carregando o resumo de hoje…" />
        ) : linhasDeHoje.length === 0 ? (
          <p className="campo-ajuda">Sem movimento hoje — nada a copiar.</p>
        ) : linhasDeHoje.map((l) => (
          <div key={l.id} className="linha-resumo">
            <span>{l.nome || '—'}</span>
            <BotaoCopiar
              rotulo="Copiar resumo do dia"
              montarTexto={() => textoAtualizadoDe(l.id)}
              textoDoErro="Não foi possível atualizar o resumo: o servidor não respondeu. Nada foi copiado."
            />
          </div>
        ))}
      </section>

      {erroGrade ? (
        <div className="erro" role="alert">
          <p>{erroGrade}</p>
          <button type="button" className="btn-secundario compacto" onClick={() => setTentativaGrade((n) => n + 1)}>
            Tentar de novo
          </button>
        </div>
      ) : grade === null ? (
        <Carregando texto={visao === 'semana' ? 'Carregando a semana…' : 'Carregando o mês…'} />
      ) : grade.blocos.every((b) => b.linhas.length === 0) ? (
        <p className="em-breve">
          {vejoTudo
            ? 'Ninguém com função de vendas cadastrada. Defina as funções em '
            : 'Você não tem função de vendas (SDR ou negociador) cadastrada, então não há linha sua. Peça ao gestor em '}
          <Link to="/equipe">Equipe</Link>.
        </p>
      ) : (
        <>
          <Grade grade={grade} config={config} podeVerTudo={vejoTudo} />

          {/* ★★ Fechada por padrão. O `<details>` nativo é o que garante que o conteúdo
              não existe antes do clique — e o efeito acima só dispara com `acumuladoAberto`. */}
          <details
            className="secao secao-acumulado"
            open={acumuladoAberto}
            onToggle={(e) => setAcumuladoAberto(e.currentTarget.open)}
          >
            <summary className="secao-titulo">
              Acumulado — últimos {Number(clinica?.config?.janelaTaxasDias) || JANELA_PADRAO_DIAS} dias
              {' '}<span className="campo-ajuda">(o funil por coorte; abre sob demanda)</span>
            </summary>
            {erro90 && <p className="erro">{erro90}</p>}
            {acumuladoAberto && acumulado === null && !erro90 ? (
              <Carregando />
            ) : (
              <Acumulado acumulado={acumulado} equipe={equipe} mostrarDinheiro={mostrarDinheiro} />
            )}
          </details>
        </>
      )}

      {/* ★★★ A-7a — O LINK PARA O PLACAR SAIU: `/placar` redireciona para `/dashboard`, e um
          link para um redirecionamento é um link que mente sobre onde leva. ★ O mês contra a
          meta da clínica passou a ser a **grade diária, no fim do Dashboard**. */}
      <p className="campo-ajuda">
        O mês contra a meta da clínica está na <strong>grade diária</strong>, no fim do
        Dashboard; aqui é dia a dia, por pessoa.
      </p>

      {isGestor && (
        <DialogoMetasIndividuais
          aberto={metasIndividuaisAberto}
          aoFechar={() => setMetasIndividuaisAberto(false)}
          clinicaId={clinicaId}
          mes={mes}
          metas={metasDoMes ?? {}}
          sdrs={vendedores(equipe)}
        />
      )}
    </section>
  );
}
